"use client";

import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Filter, Camera, X, ChevronDown, ChevronUp } from "lucide-react";
import { fadeInUp, staggerContainer } from "@/utils/animations";
import { useLanguage } from "@/contexts/LanguageContext";

// Import all images from src/assets/images/ using Vite's glob import
const imageModules = import.meta.glob('/src/assets/images/**/*.{jpg,jpeg,png,webp,avif}', { 
  eager: true,
  query: '?url',
});

// Convert the modules to an array of image data
const allImages = Object.entries(imageModules).map(([path, module]) => {
  // Extract the folder name from the path (the subfolder under src/assets/images/)
  const parts = path.split('/');
  const folder = parts[4]; // because path structure: /src/assets/images/[folder]/[filename]
  const fileName = parts[5] || parts[4];
  
  // Get the image URL
  const imageUrl = (module as any).default;

  // Generate descriptive alt text based on folder and filename
  const categoryMap: Record<string, string> = {
    'black and white': 'Black and White Photography',
    'landscapes': 'Landscape Photography',
    'portrait': 'Portrait Photography',
    'religious': 'Religious Photography',
    'seasons': 'Seasonal Photography',
    'street photographies': 'Street Photography',
    'traditions': 'Traditional Photography',
  };

  const categoryName = categoryMap[folder?.toLowerCase()] || 'Fine Art Photography';
  const altText = `${categoryName} - ${fileName.split('.')[0].replace(/[0-9._]/g, ' ').trim() || 'Artistic photography by Dumitrescu'}`;
  const titleText = `${categoryName}: ${fileName.split('.')[0].replace(/[0-9._]/g, ' ').trim() || 'Artistic composition'}`;

  return {
    id: path,
    src: imageUrl,
    category: folder?.toLowerCase() || 'art',
    title: titleText,
    description: `Artistic ${categoryName.toLowerCase()} capturing beauty, emotion, and composition through expert technique and creative vision.`,
    alt: altText,
  };
});

// If there are no images, we can use a fallback
if (allImages.length === 0) {
  console.warn('No images found in src/assets/images/');
}

// Extract unique categories from the images
const allCategories = Array.from(new Set(allImages.map(img => img.category)));

type Category = "all" | string;

interface ImageData {
  id: string;
  src: string;
  category: string;
  title: string;
  description: string;
  alt: string;
}

const Gallery = () => {
  const [activeCategory, setActiveCategory] = useState<Category>("all");
  const [selectedImage, setSelectedImage] = useState<ImageData | null>(null);
  const [visibleCount, setVisibleCount] = useState(15);
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useLanguage();

  // Generate categories for filter buttons
  const categories = useMemo(() => {
    const base = [{ id: "all", label: t('gallery.allWork') }];
    const categoryLabels: Record<string, string> = {
      'black and white': 'Black & White',
      'landscapes': 'Landscapes',
      'portrait': 'Portraits',
      'religious': 'Religious',
      'seasons': 'Seasons',
      'street photographies': 'Street Photography',
      'traditions': 'Traditions',
    };
    
    const others = allCategories.map(cat => ({
      id: cat,
      label: categoryLabels[cat] || cat.charAt(0).toUpperCase() + cat.slice(1),
    }));
    return base.concat(others);
  }, [t]);

  const filteredImages = useMemo(() => {
    if (activeCategory === "all") return allImages;
    return allImages.filter((img) => img.category === activeCategory);
  }, [activeCategory]);

  // Limit displayed images
  const displayedImages = useMemo(() => {
    return filteredImages.slice(0, visibleCount);
  }, [filteredImages, visibleCount]);

  const handleImageClick = (image: ImageData) => {
    setSelectedImage(image);
  };

  const handleLoadMore = () => {
    const newCount = visibleCount + 9;
    setVisibleCount(newCount);
    setIsExpanded(true);
    
    setTimeout(() => {
      const gallerySection = document.getElementById('gallery');
      if (gallerySection) {
        window.scrollTo({
          top: gallerySection.offsetTop + gallerySection.offsetHeight - 400,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const handleShowLess = () => {
    setVisibleCount(15);
    setIsExpanded(false);
    
    setTimeout(() => {
      const gallerySection = document.getElementById('gallery');
      if (gallerySection) {
        window.scrollTo({
          top: gallerySection.offsetTop - 100,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  return (
    <section id="gallery" className="py-20 md:py-32 bg-background theme-transition">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeInUp}
          className="mb-12 md:mb-20"
        >
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-8">
            <div className="mb-8 md:mb-0 md:w-2/3">
              <h2 className="font-inter font-bold text-3xl sm:text-4xl md:text-5xl text-foreground mb-4">
                {t('gallery.title')}
              </h2>
              <p className="font-inter font-normal text-base md:text-lg text-foreground/80 max-w-2xl">
                {t('gallery.description')}
              </p>
            </div>
            <div className="md:w-1/3 flex justify-end">
              <div className="flex flex-col items-end">
                {/* Filter Label */}
                <div className="flex items-center text-foreground bg-card border border-border px-5 py-3 rounded-xl mb-4">
                  <Filter className="w-5 h-5 mr-3 text-primary" />
                  <span className="font-inter font-medium text-sm">{t('gallery.filterLabel')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-3 mb-12">
            {categories.map((category) => (
              <motion.button
                key={category.id}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  setActiveCategory(category.id);
                  setVisibleCount(15);
                  setIsExpanded(false);
                }}
                className={`font-inter font-medium text-sm px-6 py-3 rounded-xl transition-all min-h-[44px] ${
                  activeCategory === category.id
                    ? "bg-gradient-to-r from-primary to-[#FFA000] text-primary-foreground shadow-lg shadow-primary/20"
                    : "bg-card text-foreground border border-border hover:border-primary hover:text-primary hover:shadow-md hover:shadow-primary/10"
                }`}
                aria-label={`Filter by ${category.label} photography`}
                aria-pressed={activeCategory === category.id}
              >
                {category.label}
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* Image Counter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 flex items-center justify-between"
        >
          <div className="font-inter font-medium text-sm text-foreground/80">
            {t('gallery.showing').replace('{count}', displayedImages.length.toString()).replace('{total}', filteredImages.length.toString())}
            {activeCategory !== "all" && t('gallery.inCategory').replace('{category}', categories.find(c => c.id === activeCategory)?.label || activeCategory)}
          </div>
          <div className="flex items-center">
            <Camera className="w-4 h-4 text-primary mr-2" />
            <span className="font-inter font-medium text-xs text-foreground/80">
              {t('gallery.clickToView')}
            </span>
          </div>
        </motion.div>

        {/* Masonry Grid */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeCategory}
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={staggerContainer}
            className="columns-1 sm:columns-2 lg:columns-3 gap-6 space-y-6"
          >
            {displayedImages.map((image) => (
              <motion.div
                key={image.id}
                variants={fadeInUp}
                className="relative break-inside-avoid group cursor-pointer"
                onClick={() => handleImageClick(image)}
                aria-label={`View ${image.title} - ${image.description}`}
              >
                {/* Image Container */}
                <div className="relative overflow-hidden bg-card border border-border rounded-xl transition-all duration-300 group-hover:border-primary/50 group-hover:shadow-lg group-hover:shadow-primary/10">
                  {/* Image with lazy loading */}
                  <div 
                    className="w-full aspect-[3/4] transition-transform duration-500 group-hover:scale-105 bg-gray-900/10"
                    style={{
                      backgroundImage: `url(${image.src})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                    }}
                    role="img"
                    aria-label={image.alt}
                  />
                  
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-background/95 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <h3 className="font-inter font-bold text-lg text-foreground mb-2">
                        {image.title}
                      </h3>
                      <p className="font-inter font-normal text-sm text-foreground/90 mb-4">
                        {image.description}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>

        {/* Load More / Show Less Button */}
        {filteredImages.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-12 pt-8 border-t border-border text-center"
          >
            {visibleCount < filteredImages.length ? (
              <motion.button
                onClick={handleLoadMore}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="font-inter font-medium text-sm px-8 py-4 bg-gradient-to-r from-primary to-[#FFA000] text-primary-foreground rounded-xl shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-all duration-300 flex items-center mx-auto min-h-[44px]"
                aria-label="Load more fine art photography images"
              >
                <ChevronDown className="w-5 h-5 mr-2" />
                {t('gallery.loadMore')}
                <span className="ml-2 text-primary-foreground/90">({filteredImages.length - visibleCount} remaining)</span>
              </motion.button>
            ) : isExpanded ? (
              <motion.button
                onClick={handleShowLess}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="font-inter font-medium text-sm px-8 py-4 bg-card border border-border text-foreground rounded-xl hover:border-primary hover:text-primary hover:shadow-md hover:shadow-primary/10 transition-all duration-300 flex items-center mx-auto min-h-[44px]"
                aria-label="Show less fine art photography images"
              >
                <ChevronUp className="w-5 h-5 mr-2" />
                {t('gallery.showLess')}
                <span className="ml-2 text-foreground/80">(Back to 5 rows)</span>
              </motion.button>
            ) : null}
          </motion.div>
        )}

        {/* Empty State */}
        {filteredImages.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20"
          >
            <div className="max-w-md mx-auto">
              <Camera className="w-16 h-16 text-primary mx-auto mb-6 opacity-50" />
              <h3 className="font-inter font-bold text-xl text-foreground mb-3">
                {t('gallery.noImages')}
              </h3>
              <p className="font-inter font-normal text-base text-foreground/80 mb-6">
                {activeCategory === "all" 
                  ? t('gallery.emptyGallery')
                  : t('gallery.noImagesInCategory').replace('{category}', categories.find(c => c.id === activeCategory)?.label || activeCategory)}
              </p>
              {activeCategory !== "all" && (
                <button
                  onClick={() => setActiveCategory("all")}
                  className="font-inter font-medium text-sm px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity duration-300 min-h-[44px]"
                  aria-label="View all fine art photography work"
                >
                  {t('gallery.viewAllWork')}
                </button>
              )}
            </div>
          </motion.div>
        )}

        {/* Lightbox */}
        <AnimatePresence>
          {selectedImage && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm flex items-center justify-center p-4"
              onClick={() => setSelectedImage(null)}
              role="dialog"
              aria-label={`Lightbox: ${selectedImage.title}`}
              aria-modal="true"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                className="relative max-w-5xl w-full max-h-[90vh] bg-card border border-border rounded-2xl shadow-2xl shadow-black/50 overflow-hidden theme-transition"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Close Button */}
                <button
                  onClick={() => setSelectedImage(null)}
                  className="absolute top-4 right-4 z-50 w-10 h-10 bg-background/80 hover:bg-primary text-foreground hover:text-primary-foreground rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-lg border border-border hover:border-primary theme-transition min-w-[44px] min-h-[44px]"
                  aria-label="Close lightbox"
                >
                  <X className="w-5 h-5" />
                </button>

                <div className="h-full flex flex-col lg:flex-row">
                  {/* Image Section */}
                  <div className="lg:w-2/3 bg-gradient-to-br from-card to-muted flex items-center justify-center p-6">
                    <div className="relative w-full h-full max-h-[70vh] flex items-center justify-center">
                      <img
                        src={selectedImage.src}
                        alt={selectedImage.alt}
                        className="max-w-full max-h-full rounded-xl shadow-lg object-contain"
                        loading="lazy"
                        width="1200"
                        height="900"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const fallback = document.createElement('div');
                          fallback.className = 'w-full h-full bg-gradient-to-br from-card to-muted rounded-xl flex items-center justify-center';
                          fallback.innerHTML = `
                            <div class="text-center p-8">
                              <Camera class="w-16 h-16 text-primary mx-auto mb-4" />
                              <p class="font-inter font-bold text-lg text-foreground">Fine Art Photography Image</p>
                              <p class="font-inter font-normal text-sm text-foreground/80 mt-2">${selectedImage.title}</p>
                            </div>
                          `;
                          e.currentTarget.parentNode?.appendChild(fallback);
                        }}
                      />
                    </div>
                  </div>

                  {/* Details Section */}
                  <div className="lg:w-1/3 p-6 lg:p-8 bg-gradient-to-b from-card to-background overflow-y-auto max-h-[70vh]">
                    <div className="mb-6">
                      <h3 className="font-inter font-bold text-2xl text-foreground mb-2">
                        {selectedImage.title}
                      </h3>
                      <div className="inline-block mb-4">
                        <span className={`font-inter font-medium text-xs px-3 py-1.5 rounded-full ${
                          selectedImage.category.includes('portrait') 
                            ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                            : selectedImage.category.includes('landscape')
                            ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                            : selectedImage.category.includes('black')
                            ? 'bg-gray-500/20 text-gray-300 border border-gray-500/30'
                            : 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                        }`}>
                          {selectedImage.category.charAt(0).toUpperCase() + selectedImage.category.slice(1)}
                        </span>
                      </div>
                      <p className="font-inter font-normal text-base text-foreground/80 leading-relaxed">
                        {selectedImage.description}
                      </p>
                    </div>

                    {/* Technical Details */}
                    <div className="pt-4 border-t border-border">
                      <h4 className="font-inter font-bold text-sm text-foreground mb-2">Image Details</h4>
                      <ul className="space-y-1">
                        <li className="flex items-center">
                          <span className="font-inter font-medium text-xs text-foreground/80 w-24">Category:</span>
                          <span className="font-inter font-normal text-sm text-foreground">{selectedImage.category}</span>
                        </li>
                        <li className="flex items-center">
                          <span className="font-inter font-medium text-xs text-foreground/80 w-24">Type:</span>
                          <span className="font-inter font-normal text-sm text-foreground">Fine Art Photography</span>
                        </li>
                        <li className="flex items-center">
                          <span className="font-inter font-medium text-xs text-foreground/80 w-24">Style:</span>
                          <span className="font-inter font-normal text-sm text-foreground">Artistic composition</span>
                        </li>
                      </ul>
                    </div>

                    {/* Navigation Hint */}
                    <div className="pt-6 border-t border-border">
                      <div className="flex items-center text-foreground/80">
                        <div className="w-2 h-2 bg-primary rounded-full mr-3 animate-pulse" />
                        <span className="font-inter font-medium text-xs">
                          Click anywhere outside to close
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default Gallery;